import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from "@angular/core/rxjs-interop";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { MatIconModule } from "@angular/material/icon";
import { of, switchMap } from "rxjs";
import { OmniarkUiModule, SelectOption } from "@corp-ais/omniark-ui";
import { DetailAgreementRef } from "@app/shared/models/enrichment-detail.model";
import { IAgreement, LocaleString } from "@app/shared/models/agreement.model";
import { AgreementService } from "@app/shared/services/agreement.service";
import {
  ActiveLanguage,
  LanguageConfigStore,
} from "@app/shared/services/language-config.store";
import { LanguageModeService } from "@app/shared/services/language-mode.service";
import { extractMultilingual } from "@app/shared/utils/multilingual.util";
import { sanitizeTermHtml } from "@app/shared/components/term-rich-editor/word-paste.util";
import { TermRichEditor } from "@app/shared/components/term-rich-editor/term-rich-editor";
import { logger } from "@core/utils/logger";
import { AgreementContentStore } from "../../../../services/agreement-content.store";
import {
  AGREEMENT_STATUS_ROW,
  AgreementSearch,
} from "../../../../services/agreement-search";

/** Where the T&C in effect for a price comes from. */
export type PriceAgreementSource = "own" | "offering" | "missing";

export interface AgreementUsage {
  readonly count: number;
  readonly total: number;
}

/** The inline T&C form: authoring a new T&C for the price, or editing the
 *  T&C in effect (which may be shared). */
export type AgreementForm = { mode: "create" } | { mode: "edit"; id: string };

export interface AgreementDraft {
  name: LocaleString;
  description: LocaleString;
  agreementType: string;
  status: string;
}

const NEW_DRAFT: AgreementDraft = {
  name: {},
  description: {},
  agreementType: "package",
  status: "active",
};

/** The select value meaning "the package's T&C" (no T&C of its own). */
export const SAME_AS_PACKAGE = "__package__";

/**
 * Pricing tab → "Terms & Conditions" (CONTEXT.md "Price Agreement").
 *
 * "Create T&C" authors a new Agreement and binds it to the price; "Edit"
 * patches the T&C in effect, which reaches every price and package that
 * uses it.
 *
 * Presentational for the price data: the parent owns the price and writes
 * every change back. `agreementChange` carries the price's new list — `[]`
 * means "use the package's T&C" (the BFF removes the price's own entry).
 */
@Component({
  selector: "app-enrichment-price-agreement",
  standalone: true,
  imports: [CommonModule, OmniarkUiModule, MatIconModule, TermRichEditor],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-price-agreement.html",
  styleUrls: ["./enrichment-price-agreement.scss"],
})
export class EnrichmentPriceAgreement {
  private readonly agreementService = inject(AgreementService);
  private readonly agreementContent = inject(AgreementContentStore);
  private readonly languageConfig = inject(LanguageConfigStore);
  private readonly languageMode = inject(LanguageModeService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  /** The price's own Agreement; `null` or `[]` = uses the package's. */
  readonly agreements = input<DetailAgreementRef[] | null | undefined>(null);
  readonly priceName = input<string>("");
  /** The package's T&C (Term tab) — what a price without its own uses. */
  readonly offeringAgreement = input<DetailAgreementRef | null>(null);
  readonly usedBy = input<AgreementUsage | null>(null);

  readonly agreementChange = output<DetailAgreementRef[]>();

  /** The picker's server-side search; idle until it is hovered or opened. */
  readonly search = new AgreementSearch(this.agreementContent);

  // ── Language (same default as the Term tab's preview) ────────────────
  readonly languages = computed<ActiveLanguage[]>(() =>
    this.languageConfig.active(),
  );
  private readonly localeOverride = signal<string | null>(null);
  /** A manual tab pick while it stays active, else the hero language. */
  readonly locale = computed<string>(() => {
    const pick = this.localeOverride();
    return pick && this.languages().some((l) => l.locale === pick)
      ? pick
      : this.languageMode.activeLocale();
  });

  setLocale(locale: string): void {
    this.localeOverride.set(locale);
  }

  readonly own = computed<DetailAgreementRef | null>(() =>
    ownAgreement(this.agreements()),
  );
  readonly effective = computed<DetailAgreementRef | null>(() =>
    effectiveAgreement(this.agreements(), this.offeringAgreement()),
  );
  readonly source = computed<PriceAgreementSource>(() => {
    if (this.own()) return "own";
    return this.offeringAgreement() ? "offering" : "missing";
  });
  readonly effectiveName = computed(() => {
    const effective = this.effective();
    return effective ? this.nameOf(effective) : "";
  });

  /** A price that owns the package's very T&C is pinned to it: it stops
   *  following the Term tab. Only legacy data gets here — the select offers
   *  the package's T&C solely as the "package" entry. */
  readonly ownMatchesOffering = computed(() => {
    const own = this.own();
    return !!own && own.id === this.offeringAgreement()?.id;
  });

  // ── The select ───────────────────────────────────────────────────────
  /** What the price uses now, as a select value. */
  readonly currentValue = computed(() => this.own()?.id ?? SAME_AS_PACKAGE);
  readonly options = computed<SelectOption[]>(() => {
    const offering = this.offeringAgreement();
    const own = this.own();
    const options: SelectOption[] = [
      {
        value: SAME_AS_PACKAGE,
        label: offering
          ? `Package T&C · ${this.nameOf(offering)}`
          : "Package T&C · none set yet",
      },
    ];
    for (const a of this.search.hits()) {
      if (a.id === offering?.id && a.id !== own?.id) continue;
      options.push({
        value: a.id,
        label: this.search.label(a.name, this.labelOf(a)),
      });
    }
    if (own && !options.some((o) => o.value === own.id))
      options.push({ value: own.id, label: this.nameOf(own) });
    const status = this.search.statusRow();
    if (status) options.push(status);
    return options;
  });

  // ── The preview: T&C in effect ───────────────────────────────────────
  private readonly effectiveContent = this.contentOf(
    computed(() => this.effective()?.id ?? ""),
  );
  /** Names for the select: the package's T&C and the price's own, which
   *  need not be among the search hits. */
  private readonly offeringContent = this.contentOf(
    computed(() => this.offeringAgreement()?.id ?? ""),
  );
  private readonly ownContent = this.contentOf(
    computed(() => this.own()?.id ?? ""),
  );
  /** The full agreement of the T&C in effect, once it has loaded. */
  readonly effectiveDetail = computed<IAgreement | null>(() => {
    const a = this.effectiveContent();
    return a && a.id === this.effective()?.id ? a : null;
  });
  readonly previewLoading = computed(
    () => !!this.effective() && !this.effectiveDetail(),
  );
  readonly previewHtml = computed<SafeHtml | null>(() => {
    const a = this.effectiveDetail();
    if (!a) return null;
    const body = sanitizeTermHtml(
      extractMultilingual(a.description, this.locale()),
    );
    return body.trim() ? this.sanitizer.bypassSecurityTrustHtml(body) : null;
  });

  // ── Create / edit form ───────────────────────────────────────────────
  readonly form = signal<AgreementForm | null>(null);
  readonly draft = signal<AgreementDraft>(NEW_DRAFT);
  readonly saving = signal(false);
  readonly saveFailed = signal(false);

  readonly draftHasName = computed(() => hasText(this.draft().name));
  readonly draftHasDescription = computed(() =>
    hasText(this.draft().description),
  );
  /** A new T&C needs a name and a body; an edit may clear a body, not the name. */
  readonly canSave = computed(
    () =>
      this.draftHasName() &&
      (this.form()?.mode === "edit" || this.draftHasDescription()),
  );
  readonly canEdit = computed(() => !!this.effectiveDetail());

  openCreate(): void {
    this.draft.set(NEW_DRAFT);
    this.saveFailed.set(false);
    this.form.set({ mode: "create" });
  }

  openEdit(): void {
    const a = this.effectiveDetail();
    if (!a) return;
    this.draft.set({
      name: { ...a.name },
      description: { ...a.description },
      agreementType: a.agreementType ?? "",
      status: a.status ?? "",
    });
    this.saveFailed.set(false);
    this.form.set({ mode: "edit", id: a.id });
  }

  closeForm(): void {
    this.form.set(null);
  }

  setDraftName(locale: string, value: unknown): void {
    this.draft.update((d) => ({
      ...d,
      name: { ...d.name, [locale]: String(value ?? "") },
    }));
  }

  setDraftDescription(locale: string, html: string): void {
    this.draft.update((d) => ({
      ...d,
      description: { ...d.description, [locale]: html },
    }));
  }

  setDraftField(field: "agreementType" | "status", value: unknown): void {
    this.draft.update((d) => ({ ...d, [field]: String(value ?? "") }));
  }

  save(): void {
    const form = this.form();
    if (!form || !this.canSave() || this.saving()) return;
    const d = this.draft();
    const body: Partial<IAgreement> = {
      name: filledLocales(d.name),
      description: filledLocales(d.description),
      agreementType: d.agreementType.trim() || undefined,
      status: d.status.trim() || undefined,
    };
    const request =
      form.mode === "create"
        ? this.agreementService.createAgreement(body)
        : this.agreementService.patchAgreement(form.id, body);
    this.saving.set(true);
    this.saveFailed.set(false);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.agreementContent.put(saved);
        this.saving.set(false);
        this.form.set(null);
        if (form.mode === "create")
          this.agreementChange.emit([
            { id: saved.id, name: this.labelOf(saved) },
          ]);
      },
      error: (error: unknown) => {
        logger.error("Failed to save agreement:", error);
        this.saving.set(false);
        this.saveFailed.set(true);
      },
    });
  }

  choose(value: unknown): void {
    if (typeof value !== "string" || !value || value === AGREEMENT_STATUS_ROW)
      return;
    if (value === SAME_AS_PACKAGE) {
      this.useSameAsPackage();
      return;
    }
    if (value === this.own()?.id) return;
    const listed = this.search.hits().find((a) => a.id === value);
    this.agreementChange.emit([
      { id: value, name: listed ? this.labelOf(listed) : value },
    ]);
  }

  useSameAsPackage(): void {
    if (this.own()) this.agreementChange.emit([]);
  }

  /** The full agreement for an id signal, via the shared per-id cache. */
  private contentOf(id: () => string) {
    return toSignal(
      toObservable(computed(id)).pipe(
        switchMap((v) => (v ? this.agreementContent.get(v) : of(null))),
      ),
      { initialValue: null as IAgreement | null },
    );
  }

  private nameOf(ref: DetailAgreementRef): string {
    const known = [this.ownContent(), this.offeringContent()].find(
      (a) => a?.id === ref.id,
    );
    return this.nameText(known?.name) || ref.name || ref.id;
  }

  private labelOf(a: IAgreement): string {
    return this.nameText(a.name) || a.id;
  }

  /** The name in the preview language, then Thai, then English. */
  private nameText(name: LocaleString | undefined): string {
    return (
      extractMultilingual(name, this.locale()) ||
      extractMultilingual(name, "th-TH") ||
      extractMultilingual(name, "en-US")
    );
  }
}

function hasText(bag: LocaleString): boolean {
  return Object.values(bag).some((v) => !!v?.trim());
}

/** Drop blank locales so a save never writes an empty translation. */
function filledLocales(bag: LocaleString): LocaleString {
  return Object.fromEntries(Object.entries(bag).filter(([, v]) => !!v?.trim()));
}

function ownAgreement(
  list: DetailAgreementRef[] | null | undefined,
): DetailAgreementRef | null {
  return list?.find((a) => !!a?.id) ?? null;
}

export function effectiveAgreement(
  own: DetailAgreementRef[] | null | undefined,
  offering: DetailAgreementRef | null,
): DetailAgreementRef | null {
  return ownAgreement(own) ?? offering;
}
